/**
 * Login page — on-brand, ContactRate design system.
 *
 * Layout: centered card, 440px wide, logo + tagline above form.
 * Colors, fonts, radii from design tokens.
 */

import React, { useState, FormEvent } from 'react';
import { ContactRateLogo } from '../components/ContactRateLogo.js';

interface Props {
  onLogin: (email: string, password: string) => Promise<void>;
  error?: string | null;
  /** 'operator' renders the admin portal variant with distinct heading + badge */
  variant?: 'client' | 'operator';
}

export function LoginPage({ onLogin, error, variant = 'client' }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await onLogin(email.trim(), password);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Login failed — try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayError = error ?? formError;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ background: '#FAFBFC' }}
    >
      {/* Card */}
      <div
        className="w-full max-w-[440px] bg-white rounded-[16px] p-8"
        style={{ boxShadow: '0 8px 40px -12px rgba(13,23,41,0.18)' }}
      >
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <ContactRateLogo height={30} />
        </div>

        {/* Tagline */}
        <div className="text-center mb-8">
          {variant === 'operator' && (
            <span
              className="inline-block mb-2.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em]"
              style={{
                background: 'rgba(46,102,224,0.08)',
                border: '1px solid rgba(46,102,224,0.22)',
                color: '#2E66E0',
                fontFamily: '"DM Mono", monospace',
              }}
            >
              Operator
            </span>
          )}
          <h1
            className="text-[22px] font-bold leading-tight tracking-[-0.01em] text-ink"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif' }}
          >
            {variant === 'operator' ? 'Operator sign in' : 'Sign in to your workspace'}
          </h1>
          <p className="mt-1.5 text-[13.5px] text-muted">
            AI voice agents working your pipeline 24/7
          </p>
        </div>

        {/* Error banner */}
        {displayError && (
          <div
            className="mb-5 px-4 py-3 rounded-[10px] text-[13px] font-medium"
            style={{
              background: 'rgba(239,68,68,0.07)',
              border: '1px solid rgba(239,68,68,0.25)',
              color: '#DC2626',
            }}
          >
            {displayError}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-muted mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full h-10 px-3.5 text-[13.5px] text-ink rounded-[10px] outline-none transition-all"
                style={{
                  background: '#F2F4F7',
                  border: '1px solid #E5EAF2',
                }}
                onFocus={(e) => {
                  e.target.style.border = '1px solid #2E66E0';
                  e.target.style.boxShadow = '0 0 0 3px rgba(46,102,224,0.12)';
                }}
                onBlur={(e) => {
                  e.target.style.border = '1px solid #E5EAF2';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-muted mb-1.5"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-10 px-3.5 text-[13.5px] text-ink rounded-[10px] outline-none transition-all"
                style={{
                  background: '#F2F4F7',
                  border: '1px solid #E5EAF2',
                }}
                onFocus={(e) => {
                  e.target.style.border = '1px solid #2E66E0';
                  e.target.style.boxShadow = '0 0 0 3px rgba(46,102,224,0.12)';
                }}
                onBlur={(e) => {
                  e.target.style.border = '1px solid #E5EAF2';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full h-10 rounded-[10px] text-[14px] font-semibold text-white transition-colors disabled:opacity-60"
            style={{
              background: submitting ? '#2456C4' : '#2E66E0',
            }}
            onMouseEnter={(e) => {
              if (!submitting) (e.target as HTMLButtonElement).style.background = '#2456C4';
            }}
            onMouseLeave={(e) => {
              if (!submitting) (e.target as HTMLButtonElement).style.background = '#2E66E0';
            }}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {/* Footer hint */}
        <p className="mt-6 text-center text-[12px] text-muted">
          Your AI is already working.{' '}
          <span className="text-green-DEFAULT font-medium">3 live calls</span> right now.
        </p>
      </div>

      {/* Brand footnote */}
      <p className="mt-6 text-[11px] text-muted">
        ContactRate.com © {new Date().getFullYear()} — AI-powered sales enablement
      </p>
    </div>
  );
}
